<template>
  <MobileAuthLayout v-slot="{ facilityId, currentFacility, clearAuth }">
    <div class="mobile-manage-overlock">
      <div v-if="currentScreen === 'manage'" class="screen-container">
        <div class="task-header">
          <div class="task-info">
            <h2 class="task-title">{{ taskTitle }}</h2>
            <div class="task-meta">
              <span class="due-date">Due: {{ formattedDueDate }}</span>
            </div>
          </div>
        </div>

        <div v-if="error" class="error-container">
          <v-alert type="error" outlined>
            {{ error }}
            <template #append>
              <v-btn text small @click="retryLoad">Retry</v-btn>
            </template>
          </v-alert>
        </div>

        <div v-else-if="loading" class="loading-container">
          <div class="loading-content">
            <v-skeleton-loader
              v-for="n in 5"
              :key="n"
              :loading="loading"
              type="list-item"
              height="80"
              class="mb-2"
            />
          </div>
        </div>

        <div
          v-else-if="!loading && overlockUnits.length === 0"
          class="empty-state"
        >
          <div class="empty-content">
            <v-icon size="64" color="grey lighten-1"
              >mdi-information-outline</v-icon
            >
            <h3>No Units Found</h3>
            <p>There are no disabled lien units</p>
          </div>
        </div>

        <div v-else class="units-container">
          <div class="progress-header">
            <div class="progress-info">
              <h3>Units with Disabled Lien</h3>
              <span class="progress-text">
                {{ completedCount }}/{{ totalCount }} overlock
              </span>
            </div>
            <v-progress-linear
              :value="progressPercentage"
              color="success"
              height="6"
              rounded
              class="mt-2"
            />
          </div>

          <div class="units-checklist">
            <div
              v-for="(unit, index) in overlockUnits"
              :key="unit.id"
              class="unit-item"
              :class="{
                'unit-checked': unit.completed,
                'unit-locked': unit.lockedFromResponse,
              }"
            >
              <div class="unit-row">
                <div class="unit-checkbox">
                  <v-checkbox
                    v-model="unit.completed"
                    :disabled="unit.lockedFromResponse"
                    color="success"
                    dense
                    hide-details
                    @change="updateUnitStatus(unit, index)"
                  />
                </div>
                <div class="unit-details">
                  <div class="unit-main-info">
                    <span
                      class="unit-code"
                      :class="{
                        'completed-text':
                          unit.completed && !unit.lockedFromResponse,
                        'locked-text': unit.lockedFromResponse,
                      }"
                    >
                      {{ unit.name }}
                    </span>
                    <v-chip
                      v-if="unit.lockedFromResponse && isApplyOverlock"
                      x-small
                      color="error"
                      text-color="white"
                      class="ml-2"
                    >
                      <v-icon x-small left>mdi-lock</v-icon>
                      Overlocked
                    </v-chip>
                    <v-chip
                      v-if="
                        unit.completed &&
                        !isApplyOverlock &&
                        task?.resolution === 'done'
                      "
                      x-small
                      color="success"
                      text-color="white"
                      class="ml-2"
                    >
                      <v-icon x-small left>mdi mdi-lock-open-variant</v-icon>
                      Removed Overlock
                    </v-chip>
                  </div>
                  <div v-if="getUnitAccountInfo(unit)" class="unit-sub-info">
                    {{ getUnitAccountInfo(unit) }}
                  </div>
                </div>
              </div>

              <div class="unit-comment-row">
                <v-text-field
                  v-model="unit.comment"
                  placeholder="Add comment..."
                  :disabled="unit.lockedFromResponse"
                  dense
                  outlined
                  hide-details
                  class="comment-input"
                  @change="updateUnitComment(unit, index)"
                />
              </div>
            </div>
          </div>

          <div class="action-container">
            <v-btn
              :disabled="!canCompleteTask"
              :loading="submitting"
              color="success"
              large
              block
              rounded
              @click="completeTask"
            >
              {{ actionButtonText }}
            </v-btn>
          </div>
        </div>
      </div>

      <div v-if="currentScreen === 'completed'" class="screen-container">
        <div class="completion-header">
          <v-icon size="80" color="success">mdi-check-circle</v-icon>
          <h2>Task Completed</h2>
          <p class="completion-subtitle">{{ completionMessage }}</p>
        </div>

        <div v-if="completionSummary" class="completion-summary">
          <div class="summary-card">
            <div class="summary-header">
              <h3>Summary</h3>
            </div>
            <div class="summary-details">
              <div class="summary-item">
                <span class="summary-label">Task:</span>
                <span class="summary-value">{{ taskTitle }}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Action:</span>
                <span class="summary-value">{{ actionTypeText }}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Units Processed:</span>
                <span class="summary-value">{{
                  completionSummary.unitsProcessed
                }}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Completed At:</span>
                <span class="summary-value">{{
                  completionSummary.completedAt
                }}</span>
              </div>
            </div>
          </div>

          <div v-if="processedUnits.length > 0" class="processed-units">
            <h3>Processed Units</h3>
            <div class="units-summary">
              <div
                v-for="unit in processedUnits"
                :key="unit.id"
                class="processed-unit-item"
              >
                <div class="unit-summary-info">
                  <div class="unit-summary-name">{{ unit.name }}</div>
                  <div v-if="unit.comment" class="unit-summary-comment">
                    <em>"{{ unit.comment }}"</em>
                  </div>
                </div>
                <v-icon color="success" small>mdi-check</v-icon>
              </div>
            </div>
          </div>
        </div>

        <div class="completion-actions">
          <v-btn
            color="success"
            large
            block
            class="mb-3"
            @click="finishAndClose"
          >
            <v-icon left>mdi-check</v-icon>
            Finish
          </v-btn>

          <v-btn color="primary" outlined large block @click="goBackToTask">
            <v-icon left>mdi-arrow-left</v-icon>
            View Task Details
          </v-btn>
        </div>
      </div>

      <v-snackbar
        v-model="showSnackbar"
        :color="snackbarColor"
        :timeout="snackbarTimeout"
        bottom
      >
        {{ snackbarMessage }}
        <template #action="{ attrs }">
          <v-btn text v-bind="attrs" @click="showSnackbar = false">
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </div>
  </MobileAuthLayout>
</template>

<script>
import MobileAuthLayout from "../../mobile/MobileAuthLayout.vue";
import { GET_TASK, UPDATE_TASK, GET_DISABLED_LIEN_UNITS } from "../graphql";
import { formatDate } from "@tod-ui/helpers/datetime";

export default {
  name: "PageMobileManageOverlock",
  components: {
    MobileAuthLayout,
  },
  data() {
    return {
      currentScreen: "manage",
      task: null,
      overlockUnits: [],
      processedUnits: [],
      completionSummary: null,
      completionMessage: "",
      loading: false,
      submitting: false,
      error: "",
      showSnackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      snackbarTimeout: 4000,
    };
  },
  computed: {
    taskId() {
      return this.$route.params.taskId;
    },
    actionType() {
      return this.$route.path.includes("manage-overlock")
        ? "manage-overlock"
        : "remove-overlock";
    },
    isApplyOverlock() {
      return (
        this.task?.title?.toLowerCase().includes("apply") ||
        this.task?.description?.toLowerCase().includes("apply")
      );
    },
    actionTypeText() {
      return this.isApplyOverlock ? "Applied Overlocks" : "Removed Overlocks";
    },
    taskTitle() {
      return this.task?.title || "Manage Overlocks";
    },
    formattedDueDate() {
      if (!this.task?.dueAt) return "";
      return formatDate(this.task.dueAt, "UTC");
    },
    hasAnyCompletedUnits() {
      return this.overlockUnits.some((unit) => unit.completed);
    },
    completedCount() {
      return this.overlockUnits.filter((unit) => unit.completed).length;
    },
    totalCount() {
      return this.overlockUnits.length;
    },
    progressPercentage() {
      return this.totalCount > 0
        ? (this.completedCount / this.totalCount) * 100
        : 0;
    },
    canCompleteTask() {
      return (
        this.overlockUnits.length === 0 ||
        this.hasAnyCompletedUnits ||
        this.overlockUnits.every((unit) => unit.completed)
      );
    },
    actionButtonText() {
      if (this.overlockUnits.length === 0) {
        return "Mark as Done";
      }
      if (!this.isApplyOverlock && this.hasAnyCompletedUnits) {
        return "Remove Overlock";
      }
      if (this.overlockUnits.every((unit) => unit.completed)) {
        return "Complete Task";
      }
      return this.isApplyOverlock ? "Apply Overlocks" : "Remove Overlock";
    },
  },
  async mounted() {
    await this.loadTask();
  },
  methods: {
    async loadTask() {
      this.loading = true;
      this.error = "";

      try {
        const taskResult = await this.$apollo.query({
          query: GET_TASK,
          variables: { id: this.taskId },
          fetchPolicy: "network-only",
        });

        if (!taskResult.data?.findTask) {
          throw new Error("Task not found");
        }

        this.task = taskResult.data.findTask;

        const urlParams = new URLSearchParams(window.location.search);
        const facilityId =
          urlParams.get("facility") || this.task.organization.id;

        await this.loadDisabledLienUnits(facilityId);

        this.populateExistingChecklist();
      } catch (error) {
        console.error("Error loading task:", error);
        this.error = error.message || "Failed to load task. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    async loadDisabledLienUnits(facilityId) {
      try {
        if (!this.isApplyOverlock) {
          this.overlockUnits = [
            {
              id: this.task.organization.id,
              name: this.task.organization.name,
              completed: false,
              comment: "",
              required: true,
              lockedFromResponse: false,
            },
          ];
          return;
        }

        const result = await this.$apollo.query({
          query: GET_DISABLED_LIEN_UNITS,
          variables: {
            facilityId: this.task.organization.id,
          },
          fetchPolicy: "network-only",
        });

        const units = result.data?.organizationDescendants || [];

        this.overlockUnits = units.map((unit) => ({
          id: unit.id,
          name: unit.name,
          type: unit.type,
          subtype: unit.subtype,
          billingAccounts: unit.billingAccounts || [],
          completed: false,
          comment: "",
          required: true,
          lockedFromResponse: false,
        }));
      } catch (error) {
        console.error("Error loading disabled lien units:", error);
        throw new Error("Failed to load units");
      }
    },

    populateExistingChecklist() {
      if (
        this.task?.actions?.checklist &&
        Array.isArray(this.task.actions.checklist)
      ) {
        this.task.actions.checklist.forEach((checklistItem) => {
          const unit = this.overlockUnits.find(
            (u) => u.name === checklistItem.description
          );
          if (unit) {
            unit.completed = Boolean(checklistItem.completed);
            unit.comment = checklistItem.comment || "";
            if (checklistItem.completed) {
              unit.lockedFromResponse = true;
            }
          }
        });
      }
    },

    getUnitAccountInfo(unit) {
      if (!unit.billingAccounts || unit.billingAccounts.length === 0) {
        return null;
      }

      const account = unit.billingAccounts[0];
      let info = `Email: ${account.name || "N/A"}`;

      if (account.users && account.users.length > 0) {
        const user = account.users[0];
        if (user.firstName || user.lastName) {
          info += `| User : ${user.firstName || ""} ${
            user.lastName || ""
          }`.trim();
        }
      }

      return info;
    },

    updateUnitStatus(unit, index) {
      if (unit.lockedFromResponse && !unit.completed) {
        unit.completed = true;
        return;
      }

      console.log(`Unit ${unit.name} status updated to: ${unit.completed}`);
    },

    updateUnitComment(unit, index) {
      if (unit.lockedFromResponse) {
        return;
      }

      console.log(`Unit ${unit.name} comment updated to: ${unit.comment}`);
    },

    async completeTask() {
      if (!this.task) return;

      this.submitting = true;

      try {
        let resolution = "done";
        let checklistActions = [];

        const selectedUnits = this.overlockUnits.filter(
          (unit) => unit.completed && !unit.lockedFromResponse
        );
        this.processedUnits = [...selectedUnits];

        if (this.overlockUnits.length === 0) {
          resolution = "done";
          checklistActions = [];
        } else if (this.overlockUnits.every((unit) => unit.completed)) {
          resolution = "done";
          checklistActions = this.overlockUnits.map((unit) => ({
            id: unit.id,
            completed: true,
            comment: unit.comment || "",
            description: unit.name,
          }));
        } else {
          checklistActions = this.overlockUnits.map((unit) => ({
            id: unit.id,
            completed: unit.completed,
            comment: unit.comment || "",
            description: unit.name,
          }));
          resolution = this.task.resolution;
        }

        await this.$apollo.mutate({
          mutation: UPDATE_TASK,
          variables: {
            id: this.task.id,
            assigneeId: this.task.assignee.id,
            description: this.task.description,
            dueAt: this.task.dueAt,
            organizationId: this.task.organization.id,
            priority: this.task.priority,
            resolution: resolution,
            taskTypeId: this.task.taskType.id,
            title: this.task.title,
            actions: {
              checklist: checklistActions,
            },
          },
        });

        this.completionSummary = {
          unitsProcessed: selectedUnits.length,
          completedAt: new Date().toLocaleString(),
        };

        if (this.overlockUnits.length === 0) {
          this.completionMessage =
            "Task marked as completed - no units with disabled lien found.";
        } else if (this.overlockUnits.every((unit) => unit.completed)) {
          this.completionMessage = "All overlocks completed successfully!";
        } else {
          const actionText = this.isApplyOverlock ? "applied" : "removed";
          this.completionMessage = `Overlocks ${actionText} for ${selectedUnits.length} units successfully!`;
        }

        this.currentScreen = "completed";
      } catch (error) {
        console.error("Error completing overlock task:", error);

        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
          error.graphQLErrors.forEach((graphQLError) => {
            this.showErrorMessage(graphQLError.message);
          });
        } else {
          this.showErrorMessage(
            error.message ||
              "Failed to complete overlock task. Please try again."
          );
        }
      } finally {
        this.submitting = false;
      }
    },

    showSuccessMessage(message) {
      this.snackbarMessage = message;
      this.snackbarColor = "success";
      this.snackbarTimeout = 4000;
      this.showSnackbar = true;
    },

    showErrorMessage(message) {
      this.snackbarMessage = message;
      this.snackbarColor = "error";
      this.snackbarTimeout = 6000;
      this.showSnackbar = true;
    },

    retryLoad() {
      this.loadTask();
    },

    goBackToTask() {
      this.currentScreen = "manage";
    },

    finishAndClose() {
      window.close();
      setTimeout(() => {
        window.location.href = "/";
      }, 1000);
    },
  },
};
</script>

<style scoped>
.mobile-manage-overlock {
  padding: 0;
  background-color: #f5f5f5;
  min-height: 100vh;
  min-width: 95vw;
}

.screen-container {
  padding: 0;
  margin: 0 auto;
  width: 100%;
  min-height: 100vh;
}

.task-header {
  background: white;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.task-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #1976d2;
}

.task-meta {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}

.task-id,
.due-date {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.description-label {
  font-weight: 500;
}

.error-container,
.loading-container {
  padding: 20px;
}

.loading-content {
  padding: 20px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-content h3 {
  margin: 16px 0 8px 0;
  color: #666;
}

.empty-content p {
  color: #999;
  font-size: 16px;
}

.units-container {
  padding: 20px;
}

.progress-header {
  background: white;
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.progress-info h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.progress-text {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.units-checklist {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
}

.unit-item {
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
}

.unit-item:last-child {
  border-bottom: none;
}

.unit-item.unit-checked {
  background-color: #f1f8e9;
}

.unit-item.unit-locked {
  background-color: #f8f9fa;
  border-left: 4px solid #f44336;
}

.unit-row {
  display: flex;
  align-items: flex-start;
  padding: 12px 16px 8px 16px;
}

.unit-checkbox {
  flex-shrink: 0;
  margin-right: 12px;
  margin-top: 2px;
}

.unit-details {
  flex: 1;
  min-width: 0;
}

.unit-main-info {
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.unit-code {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  line-height: 1.3;
}

.completed-text {
  color: #666 !important;
}

.locked-text {
  color: #f44336 !important;
  font-weight: 600;
}

.unit-sub-info {
  font-size: 14px;
  color: #666;
  line-height: 1.2;
}

.unit-comment-row {
  padding: 0 16px 12px 48px;
}

.comment-input {
  font-size: 14px;
}

.comment-input .v-input__slot {
  min-height: 36px !important;
  padding: 0 8px !important;
}

.comment-input input {
  font-size: 14px !important;
  padding: 8px 0 !important;
}

.comment-input.v-input--is-disabled {
  opacity: 0.6;
}

.action-container {
  position: sticky;
  bottom: 20px;
  z-index: 10;
}

.action-container .v-btn {
  height: 56px;
  font-size: 18px;
  font-weight: 600;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.completion-header {
  text-align: center;
  padding: 60px 20px 40px 20px;
  background: linear-gradient(135deg, #f5f9f5 0%, #e8f5e8 100%);
}

.completion-header h2 {
  font-size: 28px;
  font-weight: 700;
  color: #2e7d32;
  margin: 16px 0 8px 0;
}

.completion-subtitle {
  font-size: 16px;
  color: #4a4a4a;
  margin: 0;
  line-height: 1.4;
}

.completion-summary {
  padding: 20px;
}

.summary-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
}

.summary-header {
  background: #f8f9fa;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
}

.summary-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
  font-weight: 600;
}

.summary-details {
  padding: 20px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.summary-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.summary-label {
  font-weight: 500;
  color: #666;
}

.summary-value {
  font-weight: 600;
  color: #333;
}

.processed-units {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.processed-units h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
  font-weight: 600;
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

.units-summary {
  padding: 16px;
}

.processed-unit-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.processed-unit-item:last-child {
  border-bottom: none;
}

.unit-summary-info {
  flex: 1;
}

.unit-summary-name {
  font-weight: 600;
  color: #333;
  font-size: 16px;
  margin-bottom: 4px;
}

.unit-summary-comment {
  color: #666;
  font-size: 14px;
  font-style: italic;
}

.completion-actions {
  padding: 20px;
  position: sticky;
  bottom: 0;
  background: #f5f5f5;
}

.completion-actions .v-btn {
  height: 56px;
  font-size: 18px;
  font-weight: 600;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

@media (max-width: 768px) {
  .task-header {
    padding: 16px;
  }

  .task-title {
    font-size: 20px;
  }

  .task-meta {
    flex-direction: column;
    gap: 4px;
  }

  .progress-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .unit-row {
    padding: 10px 12px 6px 12px;
  }

  .unit-comment-row {
    padding: 0 12px 10px 40px;
  }

  .unit-code {
    font-size: 15px;
  }

  .unit-sub-info {
    font-size: 13px;
  }

  .completion-header {
    padding: 40px 16px 30px 16px;
  }

  .completion-header h2 {
    font-size: 24px;
  }

  .completion-summary {
    padding: 16px;
  }

  .summary-details {
    padding: 16px;
  }
}
</style>
